// offline.ts — window.backlit.offline: opt-in offline data.
//
// Nothing here runs until the app calls `await backlit.offline.enable(opts)`:
// no on-device store is opened at load (initOfflineApp only listens for the
// service worker's messages), and a page that never enables sees no change.
//
// enable() opens the two on-device stores (offline-store.ts), loads the
// persisted identity record, starts listening (below — before the /ui wait,
// so a late /ui answer still reaches onUI through the listener fan-out),
// folds in this page's /_backlit/ui answer (the transport memo of the FAB's
// load-time fetch — RM-1 — or one single-flight fetch of its own, waited for
// at most UI_WAIT_MS: past that, lie-fi, it carries on with the persisted
// record as an offline start does and marks the page offline), sweeps stale
// per-user data, then installs the offline Backend decorator via setBackend
// (offline-backend.ts). A refused enable() tears all of that down again.
//
// Boot readiness: enable() itself installs a gate Backend synchronously (when
// the page isn't enabled yet), so `offline.enable(); data.get(k)` already
// waits: the gate holds data / userdata / records / capture calls and authMe
// until that enable() settles (at most GATE_WAIT_MS), then dispatches to the
// active backend — never itself; uiConfig (the FAB, push), onStorageChange,
// authUsers and push pass straight through. When the enable() settles the
// previous backend comes back iff the gate is still active (a refusal). The
// page-level transportFailed flag (ctx.transportFailed()) is set when a
// request of this page's got no answer at all (the transport's failure
// observer — never a timeout) and cleared by any response or 2xx beat.
//
// The listeners:
//
//   - every data-plane response (transport response observer): a 2xx with
//     X-Backlit-UID is an identity PROOF; a different uid than the record's is
//     an identity change (purge the previous uid's partition); a 401 on a data
//     path while signed in purges everything (a 403 never does);
//   - every later /_backlit/ui answer: an auth-mode change purges everything,
//     `user: null` purges the per-user partitions (signed out);
//   - every heartbeat: a 2xx with X-Backlit-UID proves; a 401 quarantines the
//     signed-in uid's queued writes (Public/Accounts: revoked, 7 d; Private:
//     session_ended, 30 d) and purges;
//   - bfcache restores (pageshow persisted), the service worker's
//     `backlit:data-purged`, and other tabs' channel messages drop this page's
//     own proof and re-read the persisted record;
//   - auth.logout() (runLogoutSequence, D2): a best-effort sync (≤ 2 s), the
//     uid's unsent writes quarantined for that same user (never discarded),
//     the mirror deleted (≤ 1 s) — auth.ts navigates within a 3.5 s cap.
// A signed-out or departed uid's queued writes are quarantined too, never
// discarded; they drain again only after a heartbeat that names that same uid
// (offline-sync.ts), and expire after 30 d (revoked: 7 d).
//
// enable() also sweeps expired queued writes and starts the drain
// (offline-sync.ts); a repeat enable() only swaps the options. disable() lets
// the request on the wire finish, then stops the drain (data kept); clear()
// does the same, then deletes both stores. sync() asks the drain for a pass.
// While enabled, offline (online === false) and visible, the reachability
// probe (offline-probe.ts) checks for the connection's return and, on an
// answer, puts the page online and runs the drain once.
// whenSynced() waits — never rejecting — until nothing more can be sent now:
// no decorator write in flight, no pass running, and every op left held for a
// server limit, quarantined, or waiting on a 401 hold; disable(), clear(),
// logout, an identity purge or its timeoutMs resolve it early. whenReady()
// resolves the status once the enable() in flight (if any) has settled.
// peek() / peekJSON() / cached() read what this device holds without a request
// (offline-backend.ts handle.device), after the enable() in flight settles (at
// most GATE_WAIT_MS); with no live decorator they answer null / an empty,
// incomplete page.
// pin() / unpin() / pins() keep prefixes of the current partitions out of LRU
// eviction (the mirror's "pins" meta key; a purge of the partition drops them,
// and enable() drops those of partitions the identity no longer uses). The
// mirror reports each committed eviction (onEvict); watchers that asked for
// "evicted" get them per store and reason on a 0 ms timer, this page's
// partitions only, never relayed to other tabs. enable({ persist: true })
// asks navigator.storage.persist() once per page; status().persisted is the
// browser's last answer. prefetch() warms the device copy of a prefix or of
// keys, one request at a time (offline-prefetch.ts).
//
// In-memory identity state updates synchronously as signals arrive; every
// store write they imply (purges, persists) runs on ONE serial promise queue,
// so a purge can never be overtaken by the write it was meant to precede.
//
// The purge epoch (Rule D) covers the writes that don't run on that queue: a
// counter every purge path advances synchronously, before its first await
// (purgeAll, dropUser, purgeStale when stale, clear(), the logout sequence, a
// service-worker or bfcache proof drop, another tab's purge / identity
// message). Every mirror write — a read's write-through or tombstone, a list
// page, a realtime frame, a drain commit or drop — captures ctx.epoch() when
// its request starts and re-checks it synchronously right before the store
// call, so nothing that was in flight at a purge lands after it (or recreates
// a deleted database under the old user's partition). enable() also sweeps
// orphaned per-user partitions (any u:<uid> but the record's).
//
// MUST-MATCH (pinned by sdk/test/offline-mustmatch.test.ts against
// cmd/edge/backlit-sw.js): the SW_MSG_* message types and the APP_* cache
// names the service worker writes.

import { BacklitError, type BacklitErrorCode } from "./errors.js";
import {
  getBackend,
  setBackend,
  type Backend,
  type BacklitUser,
  type BlobResult,
  type ListOptions,
  type ListPage,
  type RecordResult,
  type UIConfig,
  type Unsubscribe,
} from "./backend.js";
import { carryTags, decodeJSON, type JSONResult, type RecordJSONResult } from "./json.js";
import { validateDataKey, validateDataPrefix } from "./validate.js";
import {
  addFailureObserver,
  addResponseObserver,
  getUIConfigMemo,
  onUIConfig,
  remoteBackend,
  responseMetaOf,
  type ResponseMeta,
} from "./transport.js";
import { onHeartbeatResult, type HeartbeatInfo } from "./heartbeat.js";
import { setBeforeLogout } from "./auth.js";
import { isBacklitHost } from "./host.js";
import { PERMISSIONS, type Permission } from "./permissions.gen.js";
import {
  applyProof,
  applyUIConfig,
  bumpGen,
  emptyIdentity,
  isStale,
  isUserPartition,
  normalizeIdentity,
  now,
  pageProofValid,
  partitionFor,
  type IdentityRecord,
  type PageProof,
} from "./offline-identity.js";
import {
  CAPTURE_UID,
  DEFAULT_MAX_BYTES,
  META_IDENTITY,
  META_SYNCED,
  META_USER,
  createMirrorStore,
  createOutbox,
  defaultIdbEnv,
  pendingCounts,
  type Evicted,
  type IdbEnv,
  type MirrorStore,
  type Outbox,
} from "./offline-store.js";
import { TAB_ID, openChannel, type ChannelMessage, type OfflineChannel, type OutgoingMessage } from "./offline-channel.js";
import {
  createOfflineBackend,
  invalidatePendingHint,
  listPending,
  readCursor,
  type OfflineBackendHandle,
  type OfflineContext,
  type ResolvedOfflineOptions,
} from "./offline-backend.js";
import {
  _armingForTest,
  _drainForTest,
  _resetSyncForTest,
  bindSync,
  clearRetryAt,
  getLastSyncedAt,
  isHeldForSignIn,
  isSyncing,
  quarantineUid,
  requestDrain,
  resumeDrain,
  setLastSyncedAt,
  startSync,
  stopDrain,
  stopSync,
  sweepExpired,
  within,
  withinValue,
} from "./offline-sync.js";
import { idleResult, prefetchArgs, runPrefetch, type PrefetchHost } from "./offline-prefetch.js";
import { PROBE_DEFAULT_MS, PROBE_MIN_MS, _setProbeForTest, attachProbe, detachProbe, probeNoteOnline, rearmProbe, type ProbeHost } from "./offline-probe.js";

// Service-worker → page messages (backlit-sw.js notifyClients).
export const SW_MSG_OFFLINE_READY = "backlit:offline-ready";
export const SW_MSG_OFFLINE_UPDATED = "backlit:offline-updated";
export const SW_MSG_DATA_PURGED = "backlit:data-purged";
// The SW's per-version app cache names and the completeness marker inside one.
export const APP_CACHE_PREFIX = "backlit-offline-v-";
export const APP_STATE_PATH = "/_backlit/offline/_state";
// Logout budgets (runLogoutSequence): the best-effort sync, the mirror delete
// (also every purge's bound on it), and the hard cap auth.ts puts on the whole
// sequence before it navigates regardless.
export const LOGOUT_SYNC_MS = 2000;
export const LOGOUT_PURGE_MS = 1000;
export const LOGOUT_CAP_MS = 3500;
// How long disable() / clear() wait for the drain to go idle, and clear() for
// each database delete.
const STOP_WAIT_MS = 10_000;
const DELETE_WAIT_MS = 5000;
// Boot readiness: how long a call made during enable() waits for it (the
// gate), and how long enable() waits for its /_backlit/ui answer.
export const GATE_WAIT_MS = 3000;
export const UI_WAIT_MS = 2000;
// How long enable() waits for the browser's persistent-storage answers, and
// how many keys one `evicted` event names (count carries the total).
const PERSIST_WAIT_MS = 500;
const EVICTED_KEYS_MAX = 100;

// --- public types (mirrored in sdk/sdk.d.ts) --------------------------------

export type OfflineStoreName = "data" | "userdata" | "records" | "capture";

export interface OfflineOptions {
  stores?: OfflineStoreName[];
  writes?: "queue" | "none";
  onConflict?: (c: OfflineConflict) => OfflineResolution | Promise<OfflineResolution>;
  readTimeoutMs?: number;
  maxBytes?: number;
  probeMs?: number;
  persist?: boolean;
}

export interface OfflineValue {
  readonly value: Blob;
  readonly contentType: string;
  readonly crc32c: string;
}

export interface OfflineConflict {
  readonly store: "data" | "userdata" | "records";
  readonly key: string;
  readonly operation: "set" | "delete";
  readonly local: OfflineValue | null;
  readonly remote: OfflineValue | null;
  readonly base: string;
}

export type OfflineResolution = { value: BodyInit; contentType: string } | "local" | "remote" | undefined;

export interface OfflineStatus {
  readonly enabled: boolean;
  readonly reason?: string;
  readonly online: boolean;
  readonly syncing: boolean;
  readonly pending: number;
  readonly held: number;
  readonly quarantined: number;
  readonly lastSyncedAt: number;
  readonly stale: boolean;
  readonly app: "off" | "installing" | "ready";
  readonly bytesUsed: number;
  readonly maxBytes: number;
  readonly persisted: boolean;
}

export interface PendingWrite {
  readonly store: OfflineStoreName;
  readonly key: string;
  readonly operation: "set" | "delete" | "create";
  readonly queuedAt: number;
  readonly state: "queued" | "held" | "quarantined";
  readonly lastError?: string;
}

export interface OfflineSyncResult {
  readonly sent: number;
  readonly conflicts: number;
  readonly failed: number;
  readonly pending: number;
}

export type OfflineEvent =
  | { readonly type: "state"; readonly status: OfflineStatus }
  | { readonly type: "queued" | "synced"; readonly store: string; readonly key: string; readonly operation: "set" | "delete" | "create" }
  | { readonly type: "conflict"; readonly store: string; readonly key: string; readonly resolution: "remote" | "local" | "merged" }
  | { readonly type: "failed"; readonly store: string; readonly key: string; readonly code: BacklitErrorCode }
  | { readonly type: "quarantined"; readonly count: number; readonly reason: string }
  | { readonly type: "discarded"; readonly count: number; readonly reason: string }
  | { readonly type: "purged"; readonly reason: string }
  | { readonly type: "app-ready" | "app-updated"; readonly version: string };

// OfflineEventNext holds the event types added after v1: they reach only a
// watch() that names them (the default set above is frozen). reason is an
// open vocabulary: "max_bytes" | "no_room" | "quota" today.
export type OfflineEventNext = {
  readonly type: "evicted";
  readonly store: string;
  readonly keys: readonly string[];
  readonly count: number;
  readonly reason: string;
};

export type OfflineEventType = (OfflineEvent | OfflineEventNext)["type"];

// OfflineEventOf narrows by type; an intersection, not Extract<>, which would
// drop the shared "queued" | "synced" member.
export type OfflineEventOf<T extends OfflineEventType> = (OfflineEvent | OfflineEventNext) & { readonly type: T };

// DeviceEntry is one offline.cached() row: hasValue says whether the value's
// bytes are on the device (crc32c "" = unknown).
export interface DeviceEntry {
  readonly key: string;
  readonly crc32c: string;
  readonly contentType: string;
  readonly size: number;
  readonly hasValue: boolean;
  readonly pending?: true;
}

export type RecordDeviceEntry = DeviceEntry & { readonly owner: string };

// DevicePage is a page answered from the device: always fromCache, always
// carrying complete.
export type DevicePage<E> = ListPage<E> & { readonly fromCache: true; readonly complete: boolean };

// DeviceStore is a store the device-view reads accept.
type DeviceStore = "data" | "userdata" | "records";

// OfflinePin is one offline.pins() row: a pinned prefix and the values under
// it this device holds now.
export interface OfflinePin {
  readonly store: DeviceStore;
  readonly prefix: string;
  readonly bytes: number;
  readonly keys: number;
}

// OfflinePrefetchOptions / Progress / Result are offline.prefetch()'s options,
// its onProgress snapshot and its answer (offline-prefetch.ts).
export interface OfflinePrefetchOptions {
  signal?: AbortSignal;
  pin?: boolean;
  maxRequests?: number;
  onProgress?(p: OfflinePrefetchProgress): void;
}

export interface OfflinePrefetchProgress {
  readonly phase: "list" | "fetch";
  readonly requests: number;
  readonly keys: number;
  readonly stored: number;
  readonly bytes: number;
  readonly totalKeys: number;
  readonly totalBytes: number;
}

export interface OfflinePrefetchResult extends OfflinePrefetchProgress {
  readonly complete: boolean;
  readonly notStored: ReadonlyArray<{ readonly key: string; readonly reason: string }>;
  readonly reason?: string;
}

export interface OfflineNamespace {
  enable(opts?: OfflineOptions): Promise<OfflineStatus>;
  disable(): Promise<void>;
  clear(): Promise<{ discarded: number }>;
  status(): Promise<OfflineStatus>;
  pending(): Promise<PendingWrite[]>;
  sync(): Promise<OfflineSyncResult>;
  whenSynced(opts?: { timeoutMs?: number }): Promise<OfflineStatus>;
  whenReady(): Promise<OfflineStatus>;
  watch(cb: (e: OfflineEvent) => void): Unsubscribe;
  watch<T extends OfflineEventType>(cb: (e: OfflineEventOf<T>) => void, opts: { events: readonly T[] }): Unsubscribe;
  peek(store: "records", key: string): Promise<RecordResult | null>;
  peek(store: "data" | "userdata", key: string): Promise<BlobResult | null>;
  peekJSON<T = unknown>(store: "records", key: string): Promise<RecordJSONResult<T> | null>;
  peekJSON<T = unknown>(store: "data" | "userdata", key: string): Promise<JSONResult<T> | null>;
  cached(store: "records", prefix?: string, opts?: ListOptions): Promise<DevicePage<RecordDeviceEntry>>;
  cached(store: "data" | "userdata", prefix?: string, opts?: ListOptions): Promise<DevicePage<DeviceEntry>>;
  pin(store: DeviceStore, prefix: string): Promise<boolean>;
  unpin(store: DeviceStore, prefix: string): Promise<boolean>;
  pins(): Promise<readonly OfflinePin[]>;
  prefetch(store: DeviceStore, target: string | readonly string[], opts?: OfflinePrefetchOptions): Promise<OfflinePrefetchResult>;
}

// --- runtime state ------------------------------------------------------------

type TestEnv = {
  idb?: IdbEnv | null;
  mirror?: MirrorStore;
  outbox?: Outbox;
  channelCtor?: (new (n: string) => BroadcastChannel) | null;
};

const ALL_STORES: readonly string[] = ["data", "userdata", "records", "capture"];
const DATA_PATH = /^\/_backlit\/(data|userdata|records|capture)/;
// The persisted record is rewritten when its identity fields change, or once
// verifiedAt has advanced this far past the last write (not on every proof).
const PERSIST_EVERY_MS = 60_000;

let env: TestEnv | null = null;
let enabled = false;
let reason = "";
let online: boolean | null = null;
let options: ResolvedOfflineOptions = resolveOptions(undefined);
let mirror: MirrorStore | null = null;
let outbox: Outbox | null = null;
let chan: OfflineChannel | null = null;
let handle: OfflineBackendHandle | null = null;
let record: IdentityRecord | null = null;
let persistedRecord: IdentityRecord | null = null;
let user: BacklitUser | null = null;
let pageProof: PageProof | null = null;
// unauthPurged: an "unauthenticated" purge already ran with no uid since —
// a repeat 401 beat (a parked session) needn't purge again.
let unauthPurged = false;
let unregister: Array<() => void> = [];
let queue: Promise<void> = Promise.resolve();
let enabling: Promise<unknown> = Promise.resolve();
// gate is the Backend enable() installed while it runs (null: none).
let gate: Backend | null = null;
// transportFailed: this page's last data-plane request (or /ui) got no
// answer at all; any answer or 2xx beat since clears it. A timeout never
// sets it.
let transportFailed = false;
// epoch is the purge epoch (see the header). Never reset: it only ever grows.
let epoch = 0;
// watchers holds one entry per watch() call; filter null means the frozen v1
// set (V1_EVENTS), else exactly the named types.
type Watcher = { fn: (e: OfflineEvent | OfflineEventNext) => void; filter: Set<string> | null };
const watchers = new Set<Watcher>();
const V1_EVENTS: ReadonlySet<string> = new Set([
  "state",
  "queued",
  "synced",
  "conflict",
  "failed",
  "quarantined",
  "discarded",
  "purged",
  "app-ready",
  "app-updated",
]);
// NEXT_EVENTS are the types added after v1 (OfflineEventNext): opt-in only.
const NEXT_EVENTS: ReadonlySet<string> = new Set(["evicted"]);
let appReady = false;
let swContainer: EventTarget | null = null;
let syncMs = LOGOUT_SYNC_MS;
let purgeMs = LOGOUT_PURGE_MS;
let capMs = LOGOUT_CAP_MS;
// stateQueued: a state computation is scheduled or running; stateDirty: it was
// asked for again after that computation began.
let stateQueued = false;
let stateDirty = false;
// syncWaiters are the pending whenSynced() calls; syncGen advances whenever a
// decorator write settles, so a snapshot whose status() began before that
// never resolves one (the check the settle schedules does). checkQueued /
// checkDirty coalesce those checks like emitState.
type SyncWaiter = { offer(s: OfflineStatus): void; release(): void };
const syncWaiters = new Set<SyncWaiter>();
let syncGen = 0;
let checkQueued = false;
let checkDirty = false;
// storagePersisted is the browser's last answer on persistent storage
// (askStorage); persistAsked: navigator.storage.persist() was already called
// on this page; storageGen voids an answer that arrives after a reset.
let storagePersisted = false;
let persistAsked = false;
let storageGen = 0;
// evictBuf holds the evictions not yet reported, per store and reason;
// evictTimer is the pending flush (flushEvicted).
type EvictBucket = { store: string; reason: string; keys: string[]; count: number };
const evictBuf = new Map<string, EvictBucket>();
let evictTimer: ReturnType<typeof setTimeout> | undefined;

// serial runs task after every task queued before it; it never rejects.
function serial(task: () => unknown): Promise<void> {
  const p = queue.then(task).then(
    () => undefined,
    () => undefined,
  );
  queue = p;
  return p;
}

// quietly awaits one store step, ignoring its failure (the next step still runs).
async function quietly(step: () => Promise<unknown>): Promise<void> {
  try {
    await step();
  } catch {
    /* best-effort */
  }
}

function emit(e: OfflineEvent | OfflineEventNext): void {
  for (const w of Array.from(watchers)) {
    if (!(w.filter ? w.filter.has(e.type) : V1_EVENTS.has(e.type))) continue;
    try {
      w.fn(e);
    } catch {
      /* one watcher never breaks another */
    }
  }
}

// onEvicted is the mirror's eviction sink (after commit): while a watcher
// asked for "evicted", it buffers the evicted keys of this page's current
// partitions per store and reason, and schedules one flush. Never relayed to
// other tabs (D-PF4).
function onEvicted(evs: readonly Evicted[]): void {
  if (!enabled || !Array.from(watchers).some((w) => w.filter?.has("evicted"))) return;
  const r = record;
  for (const e of evs) {
    if (e.p !== partitionFor(e.s, r)) continue;
    const id = e.s + "\0" + e.reason;
    let b = evictBuf.get(id);
    if (!b) evictBuf.set(id, (b = { store: e.s, reason: e.reason, keys: [], count: 0 }));
    if (b.keys.length < EVICTED_KEYS_MAX) b.keys.push(e.k);
    b.count++;
  }
  if (evictBuf.size === 0 || evictTimer !== undefined) return;
  evictTimer = setTimeout(flushEvicted, 0);
  (evictTimer as { unref?: () => void }).unref?.();
}

// flushEvicted delivers one `evicted` event per buffered store and reason.
function flushEvicted(): void {
  evictTimer = undefined;
  const bs = Array.from(evictBuf.values());
  evictBuf.clear();
  for (const b of bs) emit({ type: "evicted", store: b.store, keys: b.keys, count: b.count, reason: b.reason });
}

// askStorage reads navigator.storage.persisted() (first: a full enable) and,
// for persist:true, calls navigator.storage.persist() once per page — both
// best-effort, never rejecting. Each boolean answer lands in storagePersisted
// (a late one too, with a state event).
async function askStorage(o: ResolvedOfflineOptions, first: boolean): Promise<void> {
  const g = storageGen;
  let sm: StorageManager | undefined;
  try {
    sm = typeof navigator === "undefined" ? undefined : navigator.storage;
  } catch {
    sm = undefined;
  }
  const call = async (name: "persisted" | "persist") => {
    try {
      const f = sm?.[name];
      const v: unknown = typeof f === "function" ? await f.call(sm) : undefined;
      if (g !== storageGen || typeof v !== "boolean" || v === storagePersisted) return;
      storagePersisted = v;
      if (enabled) emitState();
    } catch {
      /* best-effort */
    }
  };
  if (first) await call("persisted");
  if (o.persist && !persistAsked) {
    persistAsked = true;
    await call("persist");
  }
}

// currentParts is the partitions the identity uses now (one per store).
function currentParts(): string[] {
  const out: string[] = [];
  for (const s of ["data", "userdata", "records"] as const) {
    const p = partitionFor(s, record);
    if (p !== null && !out.includes(p)) out.push(p);
  }
  return out;
}

// emitState is the one writer of `state` events: fire-and-forget, it schedules
// one status computation on the serial queue. A call while one is scheduled
// joins it; a call while one runs marks it dirty, and exactly one more runs
// after it — so the last event delivered reflects the settled state. Never
// await it from inside a serial task.
function emitState(): void {
  if (stateQueued) {
    stateDirty = true;
    return;
  }
  stateQueued = true;
  void serial(async () => {
    stateDirty = false;
    try {
      const g = syncGen;
      const st = await status();
      emit({ type: "state", status: st });
      offerSynced(st, g);
    } finally {
      stateQueued = false;
      if (stateDirty) emitState();
    }
  });
}

// settledNow: st, with the synchronous state read now, is settled — no
// decorator write in flight, no pass running, and nothing sendable now (every
// pending op held, or the queue on a 401 hold).
function settledNow(st: OfflineStatus): boolean {
  return (handle?.inFlightWrites() ?? 0) === 0 && !isSyncing() && (isHeldForSignIn() || st.pending <= st.held);
}

// offerSynced hands snapshot st (computed from syncGen g) to every waiter.
function offerSynced(st: OfflineStatus, g: number): void {
  if (g !== syncGen) return;
  for (const w of Array.from(syncWaiters)) w.offer(st);
}

// checkSynced schedules one snapshot for the waiters (fire-and-forget,
// coalescing like emitState; never await it from inside a serial task).
function checkSynced(): void {
  if (syncWaiters.size === 0) return;
  if (checkQueued) {
    checkDirty = true;
    return;
  }
  checkQueued = true;
  void serial(async () => {
    checkDirty = false;
    try {
      const g = syncGen;
      offerSynced(await status(), g);
    } finally {
      checkQueued = false;
      if (checkDirty) checkSynced();
    }
  });
}

function writeSettled(): void {
  syncGen++;
  checkSynced();
}

// releaseSynced resolves every waiter with the status at this moment.
function releaseSynced(): void {
  for (const w of Array.from(syncWaiters)) w.release();
}

// whenSynced is offline.whenSynced(): after enable() settles, the first
// settled snapshot (settledNow) — at once when offline mode is off; released
// early by disable(), clear(), logout or an identity purge, or at timeoutMs (a
// finite number ≥ 0; anything else: no timeout). Never rejects.
function whenSynced(opts?: { timeoutMs?: number }): Promise<OfflineStatus> {
  let ms = -1;
  try {
    const v = opts?.timeoutMs;
    if (typeof v === "number" && Number.isFinite(v) && v >= 0) ms = v;
  } catch {
    /* a throwing getter: no timeout */
  }
  return new Promise<OfflineStatus>((resolve) => {
    let done = false;
    let t: ReturnType<typeof setTimeout> | undefined;
    const finish = (st: OfflineStatus) => {
      if (done) return;
      done = true;
      clearTimeout(t);
      syncWaiters.delete(w);
      resolve(st);
    };
    const report = () => void status().then(finish);
    const w: SyncWaiter = {
      offer: (st) => void (settledNow(st) && finish(st)),
      release() {
        syncWaiters.delete(w);
        clearTimeout(t);
        report();
      },
    };
    if (ms >= 0) {
      t = setTimeout(report, ms);
      (t as { unref?: () => void }).unref?.();
    }
    void enabling.then(() => {
      if (done) return;
      if (!enabled) return report();
      syncWaiters.add(w);
      checkSynced();
    });
  });
}

function navOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

// noteOnline records reachability; the false → true transition clears the
// drain's retryAt (only — never its backoff or pause). Going offline starts the
// reachability probe (offline-probe.ts); any source going online stops it.
function noteOnline(ok: boolean): void {
  if (online === ok) return;
  const back = online === false && ok;
  online = ok;
  probeNoteOnline(ok);
  if (back) clearRetryAt();
  if (enabled) emitState();
}

// resolveOptions validates enable()'s options and applies the defaults. Every
// failure is BacklitError("invalid_argument") naming the bad option.
function resolveOptions(o: OfflineOptions | undefined): ResolvedOfflineOptions {
  const check = (ok: boolean, name: string) => {
    if (!ok) throw new BacklitError("invalid_argument", "offline.enable: invalid " + name);
  };
  check(o === undefined || (o !== null && typeof o === "object"), "options");
  const x = o ?? {};
  const list = x.stores ?? ["data", "userdata", "records"];
  check(Array.isArray(list) && list.every((s) => ALL_STORES.includes(s)), "stores");
  const writes = x.writes ?? "queue";
  check(writes === "queue" || writes === "none", "writes");
  const onConflict = x.onConflict ?? null;
  check(onConflict === null || typeof onConflict === "function", "onConflict");
  const readTimeoutMs = x.readTimeoutMs ?? 0;
  check(typeof readTimeoutMs === "number" && Number.isFinite(readTimeoutMs) && readTimeoutMs >= 0, "readTimeoutMs");
  const maxBytes = x.maxBytes ?? DEFAULT_MAX_BYTES;
  check(typeof maxBytes === "number" && Number.isFinite(maxBytes) && maxBytes > 0, "maxBytes");
  const probeMs = x.probeMs ?? PROBE_DEFAULT_MS;
  check(typeof probeMs === "number" && Number.isFinite(probeMs) && probeMs >= 0, "probeMs");
  const persist = x.persist === undefined ? false : x.persist;
  check(typeof persist === "boolean", "persist");
  return {
    stores: new Set(list),
    writes,
    onConflict,
    readTimeoutMs,
    maxBytes,
    probeMs: probeMs > 0 ? Math.max(probeMs, PROBE_MIN_MS) : 0,
    persist,
  };
}

// resetRecord is the identity after a purge: no user, same auth mode, and a
// newer generation so every earlier page proof is void.
function resetRecord(): IdentityRecord {
  const r = record ?? emptyIdentity();
  return { ...emptyIdentity(), authMode: r.authMode, gen: r.gen + 1 };
}

// persist queues a write of the in-memory record when it differs from the
// last one written (identity fields, or verifiedAt ≥ PERSIST_EVERY_MS on).
function persist(): void {
  const r = record;
  const m = mirror;
  const p = persistedRecord;
  if (!r || !m) return;
  if (
    p &&
    p.uid === r.uid &&
    p.authMode === r.authMode &&
    p.permission === r.permission &&
    p.gen === r.gen &&
    r.verifiedAt - p.verifiedAt < PERSIST_EVERY_MS
  ) {
    return;
  }
  const snap = { ...r };
  persistedRecord = snap;
  void serial(() => m.setMeta(META_IDENTITY, snap));
}

// announce persists the new record, then (in queue order, after the purge it
// follows) tells watchers and the other tabs — who re-read the record.
function announce(why: string, msg: OutgoingMessage): void {
  persist();
  const c = chan;
  void serial(() => {
    emit({ type: "purged", reason: why });
    c?.post(msg);
  });
}

// purgeAll deletes the whole mirror.
function purgeAll(why: string): void {
  epoch++;
  record = resetRecord();
  pageProof = null;
  user = null;
  persistedRecord = null;
  setLastSyncedAt(0);
  if (why === "unauthenticated") unauthPurged = true;
  const m = mirror;
  void serial(() => m?.destroy(LOGOUT_PURGE_MS));
  announce(why, { type: "purge", reason: why, scope: "all" });
  releaseSynced();
}

// dropUser purges one departed uid: its partitions (all per-user ones when
// signing out), the remembered user, and its queued writes (quarantined).
function dropUser(prev: string, why: "identity_change" | "signed_out"): void {
  epoch++;
  user = null;
  const m = mirror;
  const o = outbox;
  if (!m || !o) return;
  void serial(async () => {
    await quietly(() => (why === "signed_out" ? m.deleteUserPartitions() : m.deletePartition("u:" + prev)));
    await quietly(() => m.deleteMeta(META_USER));
    await quietly(() => quarantineUid(prev, why));
  });
  const r = record ?? emptyIdentity();
  announce(
    why,
    why === "signed_out"
      ? { type: "purge", reason: why, scope: "user", uid: prev }
      : { type: "identity", uid: r.uid, gen: r.gen },
  );
  releaseSynced();
}

// proof applies one identity statement this page received itself.
function proof(uid: string, date: number): void {
  const res = applyProof(record, { uid, date }, now());
  record = res.record;
  pageProof = { uid, gen: res.record.gen };
  unauthPurged = false;
  if (res.change === "identity_change") dropUser(res.previousUid, "identity_change");
  persist();
}

function onResponse(m: ResponseMeta): void {
  transportFailed = false;
  noteOnline(true);
  if (m.status >= 200 && m.status < 300) {
    if (m.uid !== "") proof(m.uid, m.date);
  } else if (m.status === 401 && DATA_PATH.test(m.path) && record && record.uid !== "") {
    purgeAll("unauthenticated");
  }
}

function onUI(cfg: UIConfig): void {
  const u = cfg.user;
  const ui = { authMode: cfg.authMode, user: u ? { uid: u.uid, permission: u.permission } : null };
  const date = responseMetaOf(cfg)?.date ?? 0;
  const at = now();
  let res = applyUIConfig(record, ui, date, at);
  record = res.record;
  if (res.action === "authmode_changed") {
    purgeAll("auth_mode_changed");
    // Re-apply this answer to the clean record so its user proof survives.
    res = applyUIConfig(record, ui, date, at);
    record = res.record;
  } else if (res.action === "signed_out") {
    pageProof = null;
    dropUser(res.previousUid, "signed_out");
  } else if (res.action === "identity_change") {
    dropUser(res.previousUid, "identity_change");
  }
  if (u && u.uid !== "" && record.uid === u.uid) {
    // A /ui 200 naming the user is a proof (the fallback for older servers).
    pageProof = { uid: u.uid, gen: record.gen };
    unauthPurged = false;
  }
  if (u) rememberUser(u);
  persist();
}

function onBeat(status: number, info: HeartbeatInfo): void {
  if (status === 0) {
    noteOnline(false);
    return;
  }
  noteOnline(true);
  const r = record;
  if (status >= 200 && status < 300) {
    transportFailed = false;
    if (info.uid !== "") {
      proof(info.uid, info.date);
    } else if (r && pageProofValid(pageProof, r)) {
      // An older edge states no uid: refresh freshness only for the uid this
      // page has itself proven.
      record = applyProof(r, { uid: r.uid, date: info.date }, now()).record;
      persist();
    }
  } else if (status === 401) {
    if (r && r.uid !== "") {
      const uid = r.uid;
      // Public/Accounts: a 401 is a revocation. Private (or an unknown mode —
      // never the shorter TTL): the session ended, expired or was revoked.
      void serial(() => quarantineUid(uid, r.authMode === "Public" || r.authMode === "Accounts" ? "revoked" : "session_ended"));
    } else if (unauthPurged) {
      return;
    }
    purgeAll("unauthenticated");
  }
}

async function readMeta(key: string): Promise<unknown> {
  try {
    return await mirror?.getMeta(key);
  } catch {
    return undefined;
  }
}

function asUser(x: unknown): BacklitUser | null {
  if (!x || typeof x !== "object") return null;
  const u = x as Record<string, unknown>;
  const s = (v: unknown) => (typeof v === "string" ? v : "");
  if (s(u.uid) === "") return null;
  const permission = PERMISSIONS.has(u.permission as Permission) ? (u.permission as Permission) : "";
  return { uid: s(u.uid), name: s(u.name), email: s(u.email), mobile: s(u.mobile), permission };
}

// reread adopts the persisted record + user (another tab, the SW or a bfcache
// gap may have changed them). No persisted record means the mirror was
// deleted under us: treat it as a purge of this page's identity.
async function reread(): Promise<void> {
  const rec = normalizeIdentity(await readMeta(META_IDENTITY));
  if (rec) {
    record = rec;
    persistedRecord = { ...rec };
  } else {
    record = resetRecord();
    persistedRecord = null;
  }
  user = asUser(await readMeta(META_USER));
  setLastSyncedAt(Number(await readMeta(META_SYNCED)) || 0);
}

// dropPageProof: this page's own proof no longer counts (bfcache restore, or
// the SW deleted the mirror). Re-read, advance the generation, persist.
function dropPageProof(why: "bfcache" | "service_worker"): void {
  epoch++;
  pageProof = null;
  void serial(async () => {
    await reread();
    record = bumpGen(record ?? emptyIdentity());
    persist();
    if (why === "service_worker") emit({ type: "purged", reason: why });
  });
  if (why === "bfcache") emitState();
}

function onFailure(): void {
  transportFailed = true;
}

function onPageShow(e: Event): void {
  if ((e as PageTransitionEvent).persisted === true) dropPageProof("bfcache");
}

// onChannel: another tab changed the identity or purged — re-read what it
// persisted (it persists before posting) and drop this page's proof.
// A `reachable` (another tab's probe got an answer) puts this page online
// without kicking a drain.
function onChannel(msg: ChannelMessage): void {
  if (msg.type === "reachable") return noteOnline(true);
  if (msg.type !== "purge" && msg.type !== "identity") return;
  epoch++;
  pageProof = null;
  void serial(async () => {
    await reread();
    if (msg.type === "purge") emit({ type: "purged", reason: msg.reason });
  });
}

function rememberUser(u: BacklitUser): void {
  const m = mirror;
  const p = user;
  if (!m || u.uid === "") return;
  if (p && p.uid === u.uid && p.name === u.name && p.email === u.email && p.mobile === u.mobile && p.permission === u.permission) {
    return;
  }
  const snap = { uid: u.uid, name: u.name, email: u.email, mobile: u.mobile, permission: u.permission };
  user = snap;
  void serial(() => m.setMeta(META_USER, snap));
}

// cachedUser is D4: the remembered user, only for the record's own uid and
// only while the record is not stale.
function cachedUser(): BacklitUser | null {
  const r = record;
  const u = user;
  if (!enabled || !r || !u || r.uid === "" || u.uid !== r.uid || isStale(r, now())) return null;
  return { ...u };
}

// purgeStale deletes every per-user partition once the record is stale (D3).
// A record stale at call time advances the epoch there and then.
function purgeStale(): Promise<void> {
  const r0 = record;
  if (r0 && r0.uid !== "" && isStale(r0, now())) epoch++;
  return serial(async () => {
    const r = record;
    const m = mirror;
    if (!m || !r || r.uid === "" || !isStale(r, now())) return;
    user = null;
    const n = await m.deleteUserPartitions();
    await quietly(() => m.deleteMeta(META_USER));
    if (n > 0) emit({ type: "purged", reason: "stale" });
  });
}

// runLogoutSequence is auth.logout()'s hook (D2); it never rejects:
//   1. a best-effort sync, at most syncMs — skipped offline, or with nothing
//      of this uid's (or a queued capture) to send;
//   2. the drain stops (the request on the wire finishes) and the uid's unsent
//      writes are quarantined for that same user — kept, never discarded;
//   3. the mirror is deleted, at most purgeMs (the one mirror purge of a
//      logout), and the other tabs are told.
// auth.ts navigates when it settles, or at capMs regardless.
async function runLogoutSequence(): Promise<void> {
  epoch++;
  const uid = record?.uid ?? "";
  try {
    if (
      navOnline() &&
      (await (outbox as Outbox).all()).some((o) =>
        o.uid === uid ? o.state !== "quarantined" : o.uid === CAPTURE_UID && o.state === "queued",
      )
    ) {
      await within(requestDrain(true), syncMs);
    }
    void stopDrain(0);
    if (uid) await quarantineUid(uid, "logout");
  } catch {
    /* the purge still runs */
  }
  const m = mirror;
  const c = chan;
  // Again: a write begun during the sync above must not land either.
  epoch++;
  record = resetRecord();
  persistedRecord = record; // nothing to write back: the database is going away
  pageProof = null;
  user = null;
  setLastSyncedAt(0);
  await within(
    serial(async () => {
      await m?.destroy(LOGOUT_PURGE_MS);
      c?.post({ type: "purge", reason: "logout", scope: "all" });
      emit({ type: "purged", reason: "logout" });
    }),
    purgeMs,
  );
  releaseSynced();
}

async function deriveApp(): Promise<OfflineStatus["app"]> {
  if (appReady) return "ready";
  try {
    const cs = (globalThis as { caches?: CacheStorage }).caches;
    if (!cs) return "off";
    const names = (await cs.keys()).filter((n) => n.startsWith(APP_CACHE_PREFIX));
    if (names.length === 0) return "off";
    for (const n of names) {
      const c = await cs.open(n);
      for (const req of await c.keys()) {
        if (new URL(req.url).pathname !== APP_STATE_PATH) continue;
        const res = await c.match(req);
        const st = (res ? await res.json() : null) as { complete?: unknown } | null;
        if (st && st.complete === true) return "ready";
      }
    }
    return "installing";
  } catch {
    return "off";
  }
}

// status counts the signed-in uid's queued writes (and capture's) as pending
// and its quarantined ones — never another account's. Every synchronous field
// is read after the last await, so the answer is never older than its awaits.
async function status(): Promise<OfflineStatus> {
  let n = [0, 0, 0];
  try {
    if (outbox) n = pendingCounts(await outbox.all(), record?.uid ?? "");
  } catch {
    /* report zeros */
  }
  let bytesUsed = 0;
  try {
    if (mirror) bytesUsed = await mirror.bytesUsed();
  } catch {
    /* report zero */
  }
  const app = await deriveApp();
  const r = record;
  const stale = !!r && r.uid !== "" && isStale(r, now());
  const why = reason || (stale ? "stale" : "");
  const out: OfflineStatus = {
    enabled,
    online: online ?? navOnline(),
    syncing: isSyncing(),
    pending: n[0] as number,
    held: n[2] as number,
    quarantined: n[1] as number,
    lastSyncedAt: getLastSyncedAt(),
    stale,
    app,
    bytesUsed,
    maxBytes: options.maxBytes,
    persisted: storagePersisted,
  };
  return why ? { ...out, reason: why } : out;
}

const ctx: OfflineContext = {
  get mirror() {
    return mirror as MirrorStore;
  },
  get outbox() {
    return outbox as Outbox;
  },
  options: () => options,
  identity: () => record,
  hasPageProof: () => pageProofValid(pageProof, record),
  cachedUser,
  rememberUser,
  noteOnline,
  purgeStale,
  epoch: () => epoch,
  emit,
  channel: () => chan,
  setReason(r: string) {
    if (r === reason) return;
    reason = r;
    emitState();
  },
  status,
  emitState,
  writeSettled,
  transportFailed: () => transportFailed,
  tabId: TAB_ID,
};

// probeHost is the reachability probe's view of this page.
const probeHost: ProbeHost = {
  wanted: () => enabled && online === false,
  probeMs: () => options.probeMs,
  noteOnline,
  channel: () => chan,
};

function register(): void {
  if (unregister.length > 0) return;
  unregister.push(addResponseObserver(onResponse), addFailureObserver(onFailure), onUIConfig(onUI), onHeartbeatResult(onBeat));
  if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
    const w = window;
    w.addEventListener("pageshow", onPageShow);
    unregister.push(() => w.removeEventListener("pageshow", onPageShow));
  }
  const c = openChannel(env && env.channelCtor !== undefined ? { ctor: env.channelCtor } : {});
  if (c) {
    chan = c;
    unregister.push(c.on(onChannel), () => c.close());
  }
  setBeforeLogout(runLogoutSequence, capMs);
  unregister.push(() => setBeforeLogout(null));
}

function teardown(): void {
  mirror?.onEvict(null);
  clearTimeout(evictTimer);
  evictTimer = undefined;
  evictBuf.clear();
  detachProbe();
  stopSync();
  setBackend(null);
  handle?.dispose();
  handle = null;
  for (const f of unregister) {
    try {
      f();
    } catch {
      /* keep unregistering */
    }
  }
  unregister = [];
  chan = null;
  enabled = false;
  releaseSynced();
}

function idbEnv(): IdbEnv | null {
  return env && env.idb !== undefined ? env.idb : defaultIdbEnv();
}

async function refuse(why: string): Promise<OfflineStatus> {
  reason = why;
  return status();
}

async function doEnable(o: ResolvedOfflineOptions): Promise<OfflineStatus> {
  if (enabled && mirror) {
    // A repeat call replaces the options (listeners stay registered once).
    const reprobe = options.probeMs !== o.probeMs;
    options = o;
    if (reprobe) rearmProbe();
    mirror.setMaxBytes(o.maxBytes);
    await sweepExpired();
    resumeDrain();
    await withinValue(askStorage(o, false), PERSIST_WAIT_MS, undefined);
    emitState();
    return status();
  }
  // Every refusal tears down first: it leaves no listener, channel or logout
  // hook behind.
  if (!isBacklitHost()) {
    teardown();
    return refuse("unsupported_host");
  }
  const idb = idbEnv();
  if (!env?.mirror && !env?.outbox && !idb) {
    teardown();
    return refuse("unsupported");
  }
  const m = env?.mirror ?? createMirrorStore({ idb, maxBytes: o.maxBytes });
  const ob = env?.outbox ?? createOutbox({ idb });
  const [hm, ho] = await Promise.all([m.health(), ob.health()]);
  // A store we can't open (e.g. a newer build's schema): stay online-only and
  // never delete anything.
  if (!hm.available || !ho.available) {
    teardown();
    return refuse("storage_unavailable");
  }
  const storage = askStorage(o, true);
  const loaded = normalizeIdentity(await m.getMeta(META_IDENTITY));
  mirror = m;
  m.onEvict(onEvicted);
  outbox = ob;
  options = o;
  online = navOnline();
  record = loaded ?? emptyIdentity();
  persistedRecord = loaded;
  user = asUser(await m.getMeta(META_USER));
  setLastSyncedAt(Number(await m.getMeta(META_SYNCED)) || 0);
  pageProof = null;
  unauthPurged = false;
  bindSync(ctx);
  // Registered before the /ui wait, so a late /ui answer (and every response
  // meanwhile) reaches onUI / onResponse through the listener fan-out.
  register();
  const memo = getUIConfigMemo();
  if (memo) {
    onUI(memo); // answered before we listened
  } else {
    // A fetched answer reached onUI via the listener. A rejection is an
    // offline start: keep the persisted record. Past UI_WAIT_MS (lie-fi) we
    // carry on offline; the answer is applied whenever it arrives.
    const late = {};
    const got = await withinValue<unknown>(remoteBackend.uiConfig().catch(() => null), UI_WAIT_MS, late);
    if (got === late) noteOnline(false);
  }
  await purgeStale();
  // The orphan sweep: every per-user partition but the record's own uid's.
  await serial(() => m.deleteUserPartitionsExcept(record?.uid ?? ""));
  // …and the pins of any partition the identity doesn't use now.
  await serial(() => m.retainPins(currentParts()));
  await sweepExpired();
  m.setMaxBytes(o.maxBytes);
  handle = createOfflineBackend(remoteBackend, ctx);
  setBackend(handle.backend);
  enabled = true;
  reason = "";
  persist();
  emitState();
  startSync(ctx);
  attachProbe(probeHost);
  await withinValue(storage, PERSIST_WAIT_MS, undefined);
  return status();
}

// GATED are the Backend methods a gate holds while enable() runs; every other
// method (uiConfig — the FAB and push call it —, onStorageChange, authUsers,
// push*) passes straight through.
const GATED = [
  "authMe",
  "dataGet",
  "dataPut",
  "dataPutIfMatch",
  "dataDelete",
  "dataList",
  "dataGetBatch",
  "userdataGet",
  "userdataPut",
  "userdataPutIfMatch",
  "userdataDelete",
  "userdataList",
  "userdataGetBatch",
  "captureCreate",
  "captureUpdate",
  "captureGet",
  "captureList",
  "captureDelete",
  "recordsGet",
  "recordsPut",
  "recordsPutIfMatch",
  "recordsDelete",
  "recordsList",
  "recordsGetBatch",
  "recordsReassign",
] as const satisfies ReadonlyArray<keyof Backend>;

type Methods = Record<string, (...a: unknown[]) => Promise<unknown>>;

// installGate puts a gate Backend in front of the active one, synchronously,
// for the enable() run it is then handed: a GATED call waits for that run (at
// most GATE_WAIT_MS), then dispatches to the backend active by then — never to the
// gate itself (remoteBackend instead). The returned promise settles with the
// run, after restoring the previous backend iff the gate is still active (a
// refusal or failure); a decorator doEnable installed stays.
function installGate(): (run: Promise<OfflineStatus>) => Promise<OfflineStatus> {
  const prev = getBackend();
  let ready: Promise<unknown> = Promise.resolve();
  const g: Backend = { ...prev };
  for (const name of GATED) {
    (g as unknown as Methods)[name] = async (...a: unknown[]) => {
      await withinValue(ready, GATE_WAIT_MS, undefined);
      const b = getBackend();
      return ((b !== g ? b : remoteBackend) as unknown as Methods)[name]!(...a);
    };
  }
  gate = g;
  setBackend(g);
  return (run) => {
    ready = run.then(
      () => undefined,
      () => undefined,
    );
    return run.finally(() => {
      if (getBackend() === g) setBackend(prev);
      if (gate === g) gate = null;
    });
  };
}

// inOrder serializes enable()/disable() so concurrent calls never interleave.
function inOrder<T>(fn: () => Promise<T>): Promise<T> {
  const run = enabling.then(fn);
  enabling = run.catch(() => undefined);
  return run;
}

// eventFilter validates watch()'s opts — an object whose events is a non-empty
// array of strings, else BacklitError("invalid_argument") — and keeps only the
// names this version knows (an unknown one is ignored, not an error).
function eventFilter(opts: unknown): Set<string> {
  const events = opts !== null && typeof opts === "object" ? (opts as { events?: unknown }).events : undefined;
  if (!Array.isArray(events) || events.length === 0 || events.some((n) => typeof n !== "string")) {
    throw new BacklitError("invalid_argument", "offline.watch: events must be a non-empty array of event type names");
  }
  return new Set((events as string[]).filter((n) => V1_EVENTS.has(n) || NEXT_EVENTS.has(n)));
}

// checkStore throws invalid_argument unless store is data / userdata / records.
function checkStore(store: unknown): asserts store is DeviceStore {
  if (store !== "data" && store !== "userdata" && store !== "records") {
    throw new BacklitError("invalid_argument", 'offline: store must be "data", "userdata" or "records"');
  }
}

// device waits for the enable() in flight (at most GATE_WAIT_MS), then returns
// the live decorator's device-view reads (null: none).
async function device(): Promise<OfflineBackendHandle["device"] | null> {
  await withinValue<unknown>(enabling, GATE_WAIT_MS, undefined);
  return handle ? handle.device : null;
}

// peek is offline.peek: the device's value for key, or null. Rejects only on
// a bad argument.
async function peek(store: unknown, key: unknown): Promise<BlobResult | RecordResult | null> {
  checkStore(store);
  validateDataKey(key);
  const d = await device();
  return d ? d.peek(store, key).catch(() => null) : null;
}

// peekJSON is peek, parsed (whatever the content type): a value that isn't
// JSON rejects invalid_body.
async function peekJSON(store: unknown, key: unknown): Promise<JSONResult<unknown> | RecordJSONResult<unknown> | null> {
  const r = await peek(store, key);
  if (!r) return null;
  const v = await decodeJSON<unknown>(r.value, key as string);
  return carryTags(r, "owner" in r ? { value: v, crc32c: r.crc32c, owner: r.owner } : { value: v, crc32c: r.crc32c });
}

// cached is offline.cached: one metadata page of what the device holds under
// prefix. Rejects only on a bad argument (a cursor must be one this method or
// a page answered from the device returned).
async function cached(store: unknown, prefix: unknown = "", opts?: ListOptions): Promise<DevicePage<DeviceEntry | RecordDeviceEntry>> {
  checkStore(store);
  validateDataPrefix(prefix);
  const c: unknown = opts?.cursor;
  if (c !== undefined && c !== "") {
    if (typeof c !== "string" || c[0] !== "!") throw new BacklitError("invalid_cursor", "invalid cursor", 0);
    readCursor(c);
  }
  const empty = { items: [], next: "", fromCache: true as const, complete: false };
  const d = await device();
  return d ? d.cached(store, prefix, opts).catch(() => empty) : empty;
}

// pinEdit is offline.pin (add) / offline.unpin: after the enable() in flight
// settles (at most GATE_WAIT_MS), it edits the pin of store's current
// partition in queue order (after any purge already queued). False when
// offline mode is off, there is no partition, a purge intervened or nothing
// changed; rejects only on a bad argument.
async function pinEdit(store: unknown, prefix: unknown, add: boolean): Promise<boolean> {
  checkStore(store);
  validateDataPrefix(prefix);
  await withinValue<unknown>(enabling, GATE_WAIT_MS, undefined);
  const m = mirror;
  const p = partitionFor(store, record);
  if (!enabled || !m || p === null) return false;
  const e0 = epoch;
  let changed = false;
  await serial(async () => {
    if (epoch === e0 && mirror === m) changed = await (add ? m.pin(store, p, prefix) : m.unpin(store, p, prefix));
  });
  return changed;
}

// pins is offline.pins(): the pins of the current partitions, each with the
// values under it this device holds (counted by a metadata scan). A per-user
// partition answers under the device-read rule (a page proof or a transport
// failure, and not stale). Never rejects.
async function pins(): Promise<OfflinePin[]> {
  await withinValue<unknown>(enabling, GATE_WAIT_MS, undefined);
  const m = mirror;
  const r = record;
  if (!enabled || !m || !r) return [];
  const mayRead = (p: string) =>
    !isUserPartition(p) || ((pageProofValid(pageProof, r) || transportFailed) && !isStale(r, now()));
  try {
    const out: OfflinePin[] = [];
    for (const x of await m.pins()) {
      if (x.p !== partitionFor(x.s, r) || !mayRead(x.p)) continue;
      let bytes = 0;
      let keys = 0;
      for (const e of await m.scanMeta(x.s, x.p, x.prefix)) {
        if (!e.hasValue) continue;
        bytes += e.size;
        keys++;
      }
      out.push({ store: x.s, prefix: x.prefix, bytes, keys });
    }
    return out;
  } catch {
    return [];
  }
}

// prefetchHost is the runtime as offline.prefetch sees it.
const prefetchHost: PrefetchHost = {
  ctx,
  remote: remoteBackend,
  live: () => (enabled ? handle : null),
  pin: (s, prefix) => pinEdit(s, prefix, true),
};

// prefetch is offline.prefetch(): after the enable() in flight settles (at
// most GATE_WAIT_MS), it warms this device's copy (offline-prefetch.ts).
// Resolves reason "not_enabled" when offline mode is off; rejects only on a
// bad argument (a store left out of enable()'s stores included).
async function prefetch(store: unknown, target: unknown, opts?: unknown): Promise<OfflinePrefetchResult> {
  const a = prefetchArgs(store, target, opts);
  await withinValue<unknown>(enabling, GATE_WAIT_MS, undefined);
  const h = handle;
  if (!enabled || !h) return idleResult(a, "not_enabled");
  if (!options.stores.has(a.s)) throw new BacklitError("invalid_argument", "offline.prefetch: " + a.s + " is not an offline store");
  return runPrefetch(prefetchHost, h, a);
}

export function createOffline(): OfflineNamespace {
  return {
    enable(opts?: OfflineOptions): Promise<OfflineStatus> {
      let o: ResolvedOfflineOptions;
      try {
        o = resolveOptions(opts);
      } catch (err) {
        return Promise.reject(err);
      }
      // Not enabled yet: calls made from here until the run settles wait
      // for it (installGate). A repeat enable() only swaps the options.
      const arm = !enabled && !gate ? installGate() : null;
      const run = inOrder(async () => {
        try {
          return await doEnable(o);
        } catch {
          // Only invalid_argument ever rejects; any other failure degrades to
          // online-only (nothing is deleted).
          if (!enabled) {
            teardown();
            mirror = null;
            outbox = null;
          }
          return refuse("storage_unavailable");
        }
      });
      return arm ? arm(run) : run;
    },

    disable(): Promise<void> {
      return inOrder(async () => {
        teardown();
        reason = "disabled";
        await stopDrain(STOP_WAIT_MS);
        emitState();
      });
    },

    // clear works without enable() (an explicit call) and leaves the enabled
    // state alone: the stores are recreated lazily.
    async clear(): Promise<{ discarded: number }> {
      const idb = idbEnv();
      const m = mirror ?? env?.mirror ?? (idb ? createMirrorStore({ idb }) : null);
      const o = outbox ?? env?.outbox ?? (idb ? createOutbox({ idb }) : null);
      let discarded = 0;
      if (!isBacklitHost() || !o) return { discarded };
      epoch++;
      await stopDrain(STOP_WAIT_MS);
      const c = chan;
      await serial(async () => {
        discarded = await o.stats().then((s) => s.ops, () => 0);
        // Reset (and advance the epoch again) before the deletes, so a write
        // begun since the call can't recreate the mirror under the old user.
        epoch++;
        record = resetRecord();
        persistedRecord = null;
        pageProof = null;
        user = null;
        setLastSyncedAt(0);
        await o.destroy(DELETE_WAIT_MS, true);
        await m?.destroy(DELETE_WAIT_MS, true);
        invalidatePendingHint();
        c?.post({ type: "purge", reason: "clear", scope: "all" });
        if (discarded) emit({ type: "discarded", count: discarded, reason: "clear" });
        emit({ type: "purged", reason: "clear" });
      });
      if (enabled) resumeDrain();
      emitState();
      releaseSynced();
      return { discarded };
    },

    status,

    pending(): Promise<PendingWrite[]> {
      return listPending();
    },

    sync(): Promise<OfflineSyncResult> {
      return requestDrain(true);
    },

    whenSynced,

    // whenReady resolves once the enable() in flight (if any) settles, with
    // the status then; never rejects.
    whenReady(): Promise<OfflineStatus> {
      return enabling.then(() => status());
    },

    // watch registers cb for the frozen v1 set, or with opts.events for exactly
    // the named types (unknown names are ignored). Each call is its own entry.
    watch(cb: (e: never) => void, opts?: { events: readonly string[] }): Unsubscribe {
      const filter = opts === undefined ? null : eventFilter(opts);
      if (typeof cb !== "function") return () => {};
      const w: Watcher = { fn: (e) => (cb as (e: OfflineEvent | OfflineEventNext) => void)(e), filter };
      watchers.add(w);
      return () => {
        watchers.delete(w);
      };
    },

    peek: peek as OfflineNamespace["peek"],
    peekJSON: peekJSON as OfflineNamespace["peekJSON"],
    cached: cached as OfflineNamespace["cached"],

    pin(store: DeviceStore, prefix: string): Promise<boolean> {
      return pinEdit(store, prefix, true);
    },

    unpin(store: DeviceStore, prefix: string): Promise<boolean> {
      return pinEdit(store, prefix, false);
    },

    pins,

    prefetch: prefetch as OfflineNamespace["prefetch"],
  };
}

function onSWMessage(ev: Event): void {
  const d = (ev as MessageEvent).data as { type?: unknown; version?: unknown } | null | undefined;
  if (!d || typeof d.type !== "string") return;
  if (d.type === SW_MSG_OFFLINE_READY || d.type === SW_MSG_OFFLINE_UPDATED) {
    appReady = true;
    const version = typeof d.version === "string" ? d.version : "";
    emit({ type: d.type === SW_MSG_OFFLINE_READY ? "app-ready" : "app-updated", version });
  } else if (d.type === SW_MSG_DATA_PURGED && enabled) {
    dropPageProof("service_worker");
  }
}

// initOfflineApp runs at SDK load: it only listens for the service worker's
// offline messages (so an app-ready posted before the app subscribes is not
// lost). It never touches on-device storage. No-op off-host / without a SW.
export function initOfflineApp(): void {
  try {
    if (swContainer || typeof navigator === "undefined" || !isBacklitHost()) return;
    const sw = navigator.serviceWorker as EventTarget | undefined;
    if (!sw || typeof sw.addEventListener !== "function") return;
    sw.addEventListener("message", onSWMessage);
    swContainer = sw;
  } catch {
    /* no service worker container */
  }
}

// --- test hooks -------------------------------------------------------------------

// _setOfflineEnvForTest also turns the reachability probe off (a suite turns it
// back on with _setProbeForTest).
export function _setOfflineEnvForTest(e: TestEnv | null): void {
  env = e;
  _setProbeForTest(false);
}

// _idleForTest resolves once the serial queue, the decorator's own in-flight
// writes and a running drain have all finished.
export async function _idleForTest(): Promise<void> {
  for (;;) {
    const q = queue;
    await q;
    await handle?.settle();
    for (let d = _drainForTest(); d; d = _drainForTest()) await d;
    await _armingForTest();
    if (q === queue) return;
  }
}

export function _offlineContextForTest(): OfflineContext {
  return ctx;
}

// _setLogoutBudgetsForTest replaces the logout budgets (omitted: default).
export function _setLogoutBudgetsForTest(b: { syncMs?: number; purgeMs?: number; capMs?: number } | null): void {
  syncMs = b?.syncMs ?? LOGOUT_SYNC_MS;
  purgeMs = b?.purgeMs ?? LOGOUT_PURGE_MS;
  capMs = b?.capMs ?? LOGOUT_CAP_MS;
  if (enabled) setBeforeLogout(runLogoutSequence, capMs);
}

export function _resetOfflineForTest(): void {
  teardown();
  _setProbeForTest(false);
  _resetSyncForTest();
  try {
    swContainer?.removeEventListener("message", onSWMessage);
  } catch {
    /* ignore */
  }
  swContainer = null;
  appReady = false;
  env = null;
  reason = "";
  online = null;
  options = resolveOptions(undefined);
  mirror = null;
  outbox = null;
  record = null;
  persistedRecord = null;
  user = null;
  pageProof = null;
  unauthPurged = false;
  queue = Promise.resolve();
  stateQueued = stateDirty = checkQueued = checkDirty = false;
  syncWaiters.clear();
  enabling = Promise.resolve();
  gate = null;
  transportFailed = false;
  watchers.clear();
  storagePersisted = false;
  persistAsked = false;
  storageGen++;
  _setLogoutBudgetsForTest(null);
}
